import "./chats.scss";
import ChatList from "components/ChatList/ChatList";
import { ChatMessages } from "components/ChatList/ChatMessages";
import { useContext, useEffect, useState } from "react";
import { Avatar, Button, IconButton } from "@mui/material";
import { FaTrash } from "react-icons/fa";
import { MdOutlineClose } from "react-icons/md";
import chatsService from "services/admin/chatsService";
import { toast } from "react-toastify";
import { useGetChat } from "hooks/useGetChat";
import { toDate } from "helpers/dateHelper";
import withRouter from "hoc/withRouter";
import { SocketContext } from "context/SocketProvider";
import { queryClient } from "queryClient";
import { useNavigate } from "react-router-dom";

const ChatsPage = ({ router: { params, queryParams } }: any) => {
  const navigate = useNavigate();

  // Выбранный чат
  const [selectedChatID, setSelectedChatID] = useState<number | null>(queryParams.has("chatID") ? Number(queryParams.get("chatID")) : null);
  console.log("selectedChatID", selectedChatID);
  // Информация о чате
  const [isUserInfoOpen, setIsUserInfoOpen] = useState<boolean>(false);

  // Информация о чате
  const { data: chat } = useGetChat(selectedChatID);

  // Отправка сообщения через вебсокет
  const { send, isConnected } = useContext<any>(SocketContext);

  // Запрет скролла
  useEffect(() => {
    // Disable scroll on body
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, []);

  const sendMessage = (message: string) => {
    if (!selectedChatID) {
      toast.error("Чат не найден!");
      return;
    }
    if (isConnected) {
      send({
        event: "chat:send_message",
        data: {
          chatID: selectedChatID,
          text: message,
        },
      });
    } else {
      toast.error("WebSocket не подключен!");
    }
  };

  const deleteChat = async () => {
    if (!selectedChatID) {
      toast.error("Чат не найден!");
      return;
    }
    if (window.confirm("Вы уверены, что хотите удалить чат?")) {
      await chatsService.delete(selectedChatID);
      toast.success("Чат удален успешно!");

      // 👉 setQueriesData обновит все кэши, которые начинаются с этого ключа (включая все query варианты)
      queryClient.setQueriesData({
        queryKey: ["chats", localStorage.getItem("orgID"), chat?.channelConnection?.source, chat?.externalAccountUserName]
      }, (old: any[]) => {
        console.log("deleteChat.old", old);
        if (old) {
          return old.filter((c) => c.id !== selectedChatID);
        }
        return [];
      });

      setSelectedChatID(null);
      setIsUserInfoOpen(false);

      // Обнови url QueryParams
      window.history.pushState(null, "", `/admin/chats`);
    }
  };

  const markAsPersonalChat = async () => {
    try {
      if (!selectedChatID) {
        toast.error("Чат не найден!");
        return;
      }
      await chatsService.put(selectedChatID, { category: "PERSONAL" });
      toast.success("Чат помечен как персональный!");
      setSelectedChatID(null);
      setIsUserInfoOpen(false);
    } catch (error) {
      toast.error("Ошибка при помещении чата в персональные!");
    }
  };

  return (
    <div>
      <div id="agent-dialogs">
        <div className="chat-list-container">
          <ChatList
            selectedChatID={selectedChatID}
            setSelectedChatID={setSelectedChatID}
            isConnected={isConnected}
          />
        </div>
        <div className="chat-messages-container">
          {selectedChatID && (
            <ChatMessages
              chatID={selectedChatID}
              chat={chat}
              sendMessage={sendMessage}
              openUserInfo={() => setIsUserInfoOpen(true)}
              markAsPersonalChat={markAsPersonalChat}
              deleteChat={deleteChat}
            />
          )}
        </div>
        {isUserInfoOpen && (
          <div className="user-info-container">
            <div className="user-info-header">
              <div className="user-info-header-title">
                <span>Информация о чате</span>
              </div>
              <div className="user-info-header-close">
                <IconButton onClick={() => setIsUserInfoOpen(false)}>
                  <MdOutlineClose size={20} />
                </IconButton>
              </div>
            </div>
            <div className="user-info-content">
              <div className="flex items-center justify-center mb-4">
                <Avatar
                  src={chat?.contact?.externalUserPhoto || "/media/images/avatar.png"}
                  className="rounded-full"
                  style={{ width: 150, height: 150 }}
                />
              </div>
              <div className="user-info-name fs-14 mb-4 text-center">
                <div>{chat?.contact?.externalUserFirstName}</div>
              </div>

              <div className="user-info-chat-id fs-14 mb-2">
                <div className="items-center gap-2">
                  <div className="font-bold mb-1">Идентификатор чата:</div>
                  <div>
                    <span className="">{selectedChatID}</span>
                  </div>
                </div>
              </div>
              <div className="user-info-created-at fs-14 mb-2">
                <div className="font-bold mb-1">Дата создания:</div>
                <div>{toDate(chat?.createdAt)}</div>
              </div>
              <div className="user-info-updated-at fs-14 mb-2">
                <div className="font-bold mb-1">Дата обновления чата:</div>
                <div>{toDate(chat?.updatedAt)}</div>
              </div>
              <div className="user-info-source fs-14 mb-2">
                <div className="font-bold mb-1">Источник:</div>
                <div>{chat?.channelConnection?.source}</div>
              </div>
              <div className="mb-4">
                <div className="font-bold mb-1">Аккаунт:</div>
                <div>
                  <div>
                    <span>{chat?.externalAccountFirstName}</span>
                  </div>
                  <div>
                    <span>{chat?.externalAccountUserName}</span>
                  </div>
                  <div>
                    <span>{chat?.externalAccountID}</span>
                  </div>
                </div>
              </div>
              <div className="mb-4">
                <div className="font-bold mb-1">Пользователь:</div>
                <div>
                  <span>{chat?.externalUserFirstName}</span>
                </div>
                <div>
                  <span>{chat?.externalUserUserName}</span>
                </div>
                <div>
                  <span>{chat?.externalUserID}</span>
                </div>
              </div>
              <div className="user-info-source fs-14 mb-2">
                <div className="font-bold mb-1">Кто создал чат:</div>
                <div>{chat?.createdBy}</div>
              </div>
              <div className="user-info-status fs-14 mb-2">
                <div className="font-bold mb-1">Статус чата:</div>
                <div>{chat?.status}</div>
              </div>
            </div>
            <div className="user-info-footer">
              <Button variant="outlined" color="error" size="small" onClick={deleteChat}>
                <FaTrash size={12} className="mr-2" />
                Удалить чат
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default withRouter(ChatsPage);
